package org.vreo.devicehub.features.device.data.network

import io.ktor.client.HttpClient
import io.ktor.client.request.bearerAuth
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.URLProtocol
import io.ktor.http.contentType
import io.ktor.http.path
import kotlin.io.encoding.Base64

import org.vreo.devicehub.features.core.data.appLogger.AppLogger
import org.vreo.devicehub.features.core.data.protocolBuffer.models.ProtoBufRequestType
import org.vreo.devicehub.features.core.data.protocolBuffer.*
import org.vreo.devicehub.features.core.data.safeCall
import org.vreo.devicehub.features.core.domain.Result
import org.vreo.devicehub.features.core.domain.DataError
import org.vreo.devicehub.features.core.domain.EmptyResult
import org.vreo.devicehub.features.device.data.dto.control.LocalDeviceMessageDto
import org.vreo.devicehub.features.core.domain.device.device.AnprDevice


/**
 * Ktor local device access. Allows to access devices on local network.
 *
 * @property httpClient
 * @property controlUrl
 * @property urlProtocol
 */
class KtorLocalDeviceControl(
    private val httpClient: HttpClient,
    private val controlUrl: String,
    private val urlProtocol: URLProtocol = URLProtocol.HTTPS
) {
    /**
     * Ping device
     *
     * @param deviceIpAddress device ip address
     * @param password device password
     * @param pingId ping id
     * @param messageId message id
     * @return Result of empty on success or error on failure
     */
    suspend fun pingDevice(
        deviceIpAddress: String,
        password: String,
        pingId: Int = -1,
        messageId: Int = -1
    ): EmptyResult<DataError.Remote> {
        // Encode configuration to protobuf message bytes
        val payload = localModelToProtoBufMessage(
            request = ProtoBufRequestType.POST,
            message = OutgoingProtoBufMessage.PingMessage(pingId),
            id = messageId
        )

        // Encode protobuf message to base64 string
        val encodedPayload = when (payload) {
            is Result.Error -> {
                return Result.Error(payload.error.toRemote())
            }

            is Result.Success -> {
                payload.data
            }
        }

        // Send request to device
        val result = sendMessageToDevice(
            deviceIpAddress = deviceIpAddress,
            password = password,
            message = LocalDeviceMessageDto(
                payload = Base64.encode(encodedPayload)
            )
        )

        return when (result) {
            is Result.Success -> Result.Success(Unit)
            is Result.Error -> Result.Error(result.error)
        }
    }

    /**
     * Apply/send device configuration to the target device
     *
     * @param deviceIpAddress target device ip address
     * @param password device access password
     * @param device device configuration to be applied
     * @param messageId message id
     */
    suspend fun applyDeviceConfig(
        deviceIpAddress: String,
        password: String,
        device: AnprDevice,
        messageId: Int = -1
    ): EmptyResult<DataError.Remote> {
        // Encode configuration to protobuf message bytes
        val payload = localModelToProtoBufMessage(
            request = ProtoBufRequestType.POST,
            message = OutgoingProtoBufMessage.DeviceConfigurationMessage(device),
            id = messageId
        )

        // Encode protobuf message to base64 string
        val encodedPayload = when (payload) {
            is Result.Error -> {
                return Result.Error(payload.error.toRemote())
            }

            is Result.Success -> {
                payload.data
            }
        }

        // Send request to device
        val result = sendMessageToDevice(
            deviceIpAddress = deviceIpAddress,
            password = password,
            message = LocalDeviceMessageDto(
                payload = Base64.encode(encodedPayload)
            )
        )

        return when (result) {
            is Result.Success -> Result.Success(Unit)
            is Result.Error -> Result.Error(result.error)
        }
    }

    /**
     * Send message to device on local network.
     *
     * @param deviceIpAddress device ip address and port
     * @param password device password
     * @param message message to be sent to device
     * @return Result of local device message dto on success or error on failure
     */
    private suspend fun sendMessageToDevice(
        deviceIpAddress: String,
        password: String,
        message: LocalDeviceMessageDto
    ): Result<LocalDeviceMessageDto, DataError.Remote> {
        return runCatching {
            safeCall<LocalDeviceMessageDto>(
                execute = {
                    httpClient.post {
                        url {
                            protocol = urlProtocol
                            host = deviceIpAddress
                            path(controlUrl)
                        }
                        bearerAuth(password)
                        contentType(ContentType.Application.Json)
                        setBody(message)
                    }
                }
            )
        }.getOrElse { error ->
            AppLogger.error("Failed to send message to device, error: ${error.message}")
            Result.Error(DataError.Remote.UNKNOWN)
        }
    }
}